import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { X, ArrowDownLeft } from 'lucide-react';
import { Inversion } from '../types';
import { aNumero, AVISO_IMPORTE, PATRON_IMPORTE_POSITIVO } from '../utils/numeros';
import { useCerrarConEscape } from '../hooks/useCerrarConEscape';

interface ModalRetiroInversionProps {
  inversion: Inversion;
  onClose: () => void;
}

export const ModalRetiroInversion: React.FC<ModalRetiroInversionProps> = ({ inversion, onClose }) => {
  useCerrarConEscape(onClose);
  const { crearRetiroDesdeInversion, cuentas, retirosInversion } = useData();
  const [cuentaDestinoId, setCuentaDestinoId] = useState('');
  const [cantidad, setCantidad] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Calcular cantidad total retirada (solo informativo; valorActual ya la tiene descontada)
  const totalRetirado = retirosInversion
    .filter(r => r.inversionId === inversion.id)
    .reduce((s, r) => s + r.cantidadRetirada, 0);
  // El valorActual ya se decrementa en cada retiro (ver crearRetiroDesdeInversion),
  // por lo que representa directamente lo disponible para retirar. No hay que volver a restar totalRetirado.
  const disponibleParaRetirar = inversion.valorActual;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!cuentaDestinoId || !cantidad) {
      setError('Por favor completa todos los campos');
      return;
    }

    const cantidadNum = aNumero(cantidad);
    if (cantidadNum <= 0) {
      setError('La cantidad debe ser mayor a 0');
      return;
    }

    if (cantidadNum > disponibleParaRetirar) {
      setError(`No hay suficientes fondos para retirar (disponibles: €${disponibleParaRetirar.toFixed(2)})`);
      return;
    }

    setLoading(true);

    try {
      await crearRetiroDesdeInversion({
        inversionId: inversion.id,
        cuentaDestinoId,
        cantidad: cantidadNum,
        descripcion,
        fecha: new Date(fecha),
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Error al registrar retiro');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center capa-modal z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-full overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <ArrowDownLeft className="w-6 h-6 text-green-600" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Retirar de Inversión</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="boton-cerrar text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">
              Producto de inversión
            </label>
            <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white font-medium">
              {inversion.nombre}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Valor actual: €{inversion.valorActual.toFixed(2)} | Retirado: €{totalRetirado.toFixed(2)} | Disponible: €{disponibleParaRetirar.toFixed(2)}
            </p>
          </div>

          <div>
            <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Cuenta Destino</label>
            <select
              value={cuentaDestinoId}
              onChange={(e) => setCuentaDestinoId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            >
              <option value="">Selecciona cuenta destino</option>
              {cuentas.map(cuenta => (
                <option key={cuenta.id} value={cuenta.id}>
                  {cuenta.nombre} — €{cuenta.saldo.toFixed(2)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-center">
            <ArrowDownLeft className="w-6 h-6 text-gray-400" />
          </div>

          <div>
            <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Cantidad (€)</label>
            <input
              type="text" inputMode="decimal" pattern={PATRON_IMPORTE_POSITIVO} title={AVISO_IMPORTE}
              value={cantidad}
              onChange={(e) => setCantidad(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="0,00"
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Fecha</label>
            <input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Descripción (opcional)</label>
            <input
              type="text"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Motivo del retiro"
            />
          </div>

          <div className="flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition disabled:opacity-50"
            >
              {loading ? 'Procesando...' : 'Realizar Retiro'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
